/**
 * Barra de abas.
 *
 * Duas decisões registradas aqui:
 *
 * 1. **Uma aba só não mostra barra.** A tela fica com o conteúdo direto. Isso e
 *    a raiz aceitar `panel` (seção 1.1) são as duas metades de "nem toda tela é
 *    um cadastro com abas" — sem elas, todo resultado do gerador sai parecendo
 *    o mesmo cadastro.
 *
 * 2. **Aba não visitada não é montada** (`nzDeselectedTabPosition` fora, e o
 *    conteúdo dentro do `nz-tab`). Numa tela com seis abas e uma grade em cada,
 *    montar todas de uma vez é o que faz a tela demorar a abrir — e é o que
 *    acontece hoje.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { EDITOR_CHROME } from '../editor-chrome';
import { ScreenNode } from '../../model/node-props';
import { trackByNodeId } from '../../model/screen-index';
import { TranslateTAPipe } from '../translate-ta.pipe';
import { NodeHostComponent } from '../node-host.component';
import { NodeRenderer, RenderMode } from '../node-renderer';

@Component({
  selector: 'tem-tabs-container',
  standalone: true,
  imports: [CommonModule, NzTabsModule, NzIconModule, NodeHostComponent, TranslateTAPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (contextFields().length > 0) {
      <div class="tem-tabs__context">
        @for (field of contextFields(); track field.dataPath) {
          <div class="tem-tabs__context-item">
            <span class="tem-tabs__context-label">{{ field.labelKey | translateTA }}</span>
            <span class="tem-tabs__context-value">{{ field.dataPath }}</span>
          </div>
        }
      </div>
    }

    @if (showTabBar()) {
      <nz-tabset
        [nzSelectedIndex]="selectedIndex()"
        [nzTabBarExtraContent]="canAddTab() ? addTabButton : null"
        (nzSelectedIndexChange)="selectTab($event)">

        @for (tab of tabs(); track trackBy(0, tab)) {
          <nz-tab
            [nzTitle]="tabTitle"
            [nzDisabled]="isTabDisabled(tab)"
            (nzClick)="onTabClick(tab)">

            <ng-template #tabTitle>
              @if (tabIcon(tab).length > 0) {
                <nz-icon [nzType]="tabIcon(tab)" />
              }
              {{ tabLabel(tab) | translateTA }}
            </ng-template>

            <!-- A aba passa pelo NodeHost como qualquer outro no. Desenhar os
                 filhos dela direto daqui parecia atalho e custava caro: sem o
                 host nao ha involucro de edicao, sem involucro nao ha
                 data-node-id, e a aba virava a unica parte da tela que nao dava
                 para selecionar — nem para renomear, nem para por icone. -->
            <tem-node-host [node]="tab" [form]="form()" [mode]="mode()" />
          </nz-tab>
        }
      </nz-tabset>

      <!-- O "+" da barra. So no editor: e a acao que so faz sentido dentro de um
           container de abas, e por isso nao mora na paleta. -->
      <ng-template #addTabButton>
        <button type="button" class="tem-tabs__add" (click)="addTab()">
          <nz-icon nzType="plus" nzTheme="outline" />
          {{ 'builderScreen.tabs.add' | translateTA }}
        </button>
      </ng-template>
    } @else if (onlyTab(); as tab) {
      <!-- Uma aba so, na tela publicada: sem barra, mas ainda pelo NodeHost —
           o caminho de desenho e o mesmo, com ou sem barra. -->
      <tem-node-host [node]="tab" [form]="form()" [mode]="mode()" />
    }
  `,
  styleUrl: './tabs.renderer.scss'
})
export class TabsContainerRenderer implements NodeRenderer<'tabs'> {

  readonly node = input.required<ScreenNode<'tabs'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  readonly trackBy = trackByNodeId;

  private readonly chosenIndex = signal(0);

  readonly tabs = computed<readonly ScreenNode[]>(() => this.node().children ?? []);

  readonly contextFields = computed(() => this.node().props.contextFields ?? []);

  /**
   * Barra de abas só quando há mais de uma — EXCETO no editor.
   *
   * Na tela publicada, uma aba só não merece barra: é a metade de "nem toda
   * tela é um cadastro com abas". No editor a conta é outra: sem a barra não
   * há como ver que existe uma aba, nem clicar nela para selecioná-la e
   * renomeá-la. Quem monta a tela precisa enxergar a estrutura que está
   * montando, mesmo a parte que a tela final vai esconder.
   */
  readonly showTabBar = computed<boolean>(
    () => this.tabs().length > 1 || this.mode() === 'editPreview'
  );

  /** A unica aba, quando a barra nao aparece. */
  readonly onlyTab = computed<ScreenNode | null>(() => this.tabs()[0] ?? null);

  /** Mantém o índice dentro do intervalo mesmo se uma aba for removida. */
  readonly selectedIndex = computed<number>(() => {
    const total = this.tabs().length;
    if (total === 0) {
      return 0;
    }
    const chosen = this.chosenIndex();
    if (chosen < 0) {
      return 0;
    }
    if (chosen >= total) {
      return total - 1;
    }
    return chosen;
  });

  /**
   * O editor, quando existe. Em produção é `null` e o código abaixo nem roda.
   */
  private readonly chrome = inject(EDITOR_CHROME, { optional: true });

  selectTab(index: number): void {
    this.chosenIndex.set(index);
  }

  /**
   * Clique no título da aba.
   *
   * No editor, isto SELECIONA a aba — é como se alcançam as propriedades dela
   * (rótulo, ícone), porque o título vive na barra do `nz-tabset`, fora do
   * invólucro do nó, e numa aba com o conteúdo cheio não sobra área vazia para
   * clicar.
   *
   * Ligado no `nzClick` e não no `nzSelectedIndexChange`: aquele só dispara
   * quando o índice MUDA, e com uma aba só — que é como o container nasce —
   * clicar no título não mudava nada e a aba seguia inalcançável.
   *
   * Em produção o token do editor é `null` e este método não faz nada.
   */
  onTabClick(tab: ScreenNode): void {
    if (this.chrome === null) {
      return;
    }

    this.chrome.requestSelect(tab);
  }

  /** O "+" so aparece no editor. */
  readonly canAddTab = computed<boolean>(() => this.chrome !== null);

  addTab(): void {
    if (this.chrome === null) {
      return;
    }

    this.chrome.requestInsert(this.node(), 'tab');
  }

  tabLabel(tab: ScreenNode): string {
    const props = tab.props as { labelKey?: string };
    return props.labelKey ?? '';
  }

  tabIcon(tab: ScreenNode): string {
    const props = tab.props as { icon?: string };
    return props.icon ?? '';
  }

  isTabDisabled(tab: ScreenNode): boolean {
    const props = tab.props as { disabled?: boolean };
    return props.disabled === true;
  }

}
